import { useMemo } from 'react';

import { Button, PhoneInput } from '@parrot-software/design-system';
import { LoaderCircle, ShieldCheck } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Trans, useTranslation } from 'react-i18next';

import type { VerificationChannel } from '@identity/domain/types';
import { FieldLabel, InfoNote, StepCard } from '@identity/presentation/components/base';
import { IS_SMS_CHANNEL_ENABLED } from '@identity/presentation/constants';
import { useVerificationLinkSender } from '@identity/presentation/hooks';
import { translateIdentityError } from '@identity/presentation/utils';

import { createPhoneFormSchema } from './PhoneStep.schema';
import type { PhoneFormValues } from './PhoneStep.schema';

export type PhoneCaptureViewProps = {
  defaultPhone?: string;
  onCancel?: () => void;
  onSent: (sent: { channel: VerificationChannel; phone: string; verificationUuid?: string }) => void;
  stepLabel?: string;
};

/**
 * Capture view of the phone step. Both buttons send the same link and lead to the same wait; only
 * the channel differs, and the second one is a choice, never an automatic fallback.
 *
 * The SMS button is hidden while the channel is switched off, so today it sends one single way.
 *
 * @param {PhoneCaptureViewProps} props - Component properties
 */
export const PhoneCaptureView = ({ defaultPhone, onCancel, onSent, stepLabel }: PhoneCaptureViewProps) => {
  const { t } = useTranslation('identity');
  const { errorKey, errorRetryAfterSeconds, isSending, send } = useVerificationLinkSender();
  const resolver = useMemo(() => (yupResolver(createPhoneFormSchema()) as unknown) as Resolver<PhoneFormValues>, []);

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<PhoneFormValues>({
    defaultValues: { phone: defaultPhone ?? '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const phone = watch('phone');
  const fieldErrorKey = errors.phone?.message;
  const isBlocked = isSending || !phone || !isValidPhoneNumber(phone);

  const sendThrough = (channel: VerificationChannel) =>
    handleSubmit(async (values) => {
      const result = await send({ channel, value: values.phone });

      if (result) onSent({ channel, phone: values.phone, verificationUuid: result.verificationUuid });
    });

  return (
    <StepCard
      description={t('phone.subtitle')}
      icon={
        <span className="flex size-8 items-center justify-center rounded-full bg-bg-info">
          <ShieldCheck aria-hidden className="size-4 text-info" />
        </span>
      }
      stepLabel={stepLabel}
      title={t('phone.title')}
    >
      <form className="flex flex-col gap-6 font-sans" noValidate onSubmit={sendThrough('WHATSAPP')}>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-phone">{t('phone.phoneLabel')}</FieldLabel>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                aria-invalid={Boolean(fieldErrorKey)}
                className={cn({ '[&_button]:border-destructive [&_input]:border-destructive': Boolean(fieldErrorKey) })}
                countryIndicator="letters"
                defaultCountry="MX"
                id="identity-phone"
                international
                onBlur={field.onBlur}
                onChange={(value) => {
                  if (fieldErrorKey) clearErrors('phone');
                  field.onChange(value);
                }}
                placeholder={t('phone.phonePlaceholder')}
                value={field.value}
              />
            )}
          />
          {fieldErrorKey && <p className="text-xs text-destructive">{t(fieldErrorKey)}</p>}
        </div>

        <InfoNote>
          <Trans components={{ strong: <strong /> }} i18nKey="identity:phone.note" />
        </InfoNote>

        {errorKey && (
          <p className="text-xs text-destructive" data-testid="phone-send-error">
            {translateIdentityError(t, errorKey, errorRetryAfterSeconds)}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-start">
          {onCancel && (
            <Button onClick={onCancel} type="button" variant="outline">
              {t('phone.cancel')}
            </Button>
          )}
          {IS_SMS_CHANNEL_ENABLED && (
            <Button disabled={isBlocked} onClick={sendThrough('SMS')} type="button" variant="secondary">
              {t('phone.sendSms')}
            </Button>
          )}
          <Button disabled={isBlocked} type="submit">
            {isSending && (
              <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin" data-testid="phone-send-spinner" />
            )}
            {t('phone.sendWhatsApp')}
          </Button>
        </div>
      </form>
    </StepCard>
  );
};
